import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    CircularProgress,
    Box,
    Stack,
    IconButton,
    useTheme,
} from "@mui/material";
import { Button } from "@foodpilot/foods";
import { DragDropFileUpload } from "src/components/DragDropFileUpload";
import DeleteOutline from "@mui/icons-material/DeleteOutline";

type UploadMediaDialogProps = {
    open: boolean;
    onClose: () => void;
    onUpload: (files: File[], names: string[]) => void;
    isUploading: boolean;
    allowedMimeTypes?: string[];
};

export const UploadMediaDialog = (props: UploadMediaDialogProps) => {
    const { open, onClose, onUpload, isUploading, allowedMimeTypes } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const accept =
        allowedMimeTypes?.join(",") ||
        "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.mp4,.mov,.avi,.wmv,.mkv,.webm";
    const handleFilesSelected = (files: File[]) => {
        const newFiles = [...selectedFiles, ...files];
        setSelectedFiles(newFiles);
        const newNames = [...fileNames, ...files.map((file) => file.name)];
        setFileNames(newNames);
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0 || fileNames.some((name) => !name.trim())) return;
        await onUpload(selectedFiles, fileNames);
        resetForm();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setFileNames([]);
        setSelectedFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveFile = (indexToRemove: number) => {
        setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
        setFileNames((prev) => prev.filter((_, index) => index !== indexToRemove));
    };
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="body1" fontSize={18} fontWeight={theme.typography.fontWeightMedium}>
                    {t("settings.media_library.upload_dialog.title", "Upload Media")}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DragDropFileUpload
                        isUploading={isUploading}
                        onFilesSelected={handleFilesSelected}
                        disabled={isUploading}
                        accept={accept}
                        multiple
                    />
                    {selectedFiles.length > 0 && (
                        <Box sx={{ maxHeight: "200px", overflow: "auto" }}>
                            {selectedFiles.map((_, index) => (
                                <Box key={index} sx={{ mb: theme.spacing(1), display: "flex", alignItems: "center" }}>
                                    <TextField
                                        margin="dense"
                                        id={`name-${index}`}
                                        label={`${t("settings.media_library.upload_dialog.file_name", "File name")} ${index + 1}`}
                                        placeholder={t(
                                            "settings.media_library.upload_dialog.file_name_placeholder",
                                            "Enter file name",
                                        )}
                                        type="text"
                                        fullWidth
                                        value={fileNames[index]}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const newNames = [...fileNames];
                                            newNames[index] = e.target.value;
                                            setFileNames(newNames);
                                        }}
                                        disabled={isUploading}
                                        size="small"
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveFile(index)}
                                        disabled={isUploading}
                                        sx={{ ml: 1 }}
                                    >
                                        <DeleteOutline fontSize="small" color="error" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="ternary" onClick={handleClose} disabled={isUploading}>
                    {t("settings.media_library.upload_dialog.cancel", "Cancel")}
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="primary"
                    disabled={selectedFiles.length === 0 || fileNames.some((name) => !name.trim()) || isUploading}
                >
                    {isUploading ?
                        <CircularProgress size={24} />
                    :   t("settings.media_library.upload_dialog.upload", "Upload")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
